import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import {
  FixedSizeGrid as Grid,
  type GridChildComponentProps,
} from "react-window";
import type { ObjectAssetInfo } from "../../api";
import { ModelCard } from "./ModelCard";

const CELL_GAP = 6;
/** Target tile size — 320px panel fits 4 columns; wider panels add more. */
const COLUMN_MIN_WIDTH = 72;

interface CellData {
  models: ObjectAssetInfo[];
  columnCount: number;
  onDragStart: (model: ObjectAssetInfo, e: MouseEvent) => void;
}

function Cell({
  columnIndex,
  rowIndex,
  style,
  data,
}: GridChildComponentProps<CellData>) {
  const { models, columnCount, onDragStart } = data;
  const index = rowIndex * columnCount + columnIndex;

  if (index >= models.length) {
    return <div style={style} />;
  }

  const model = models[index];
  return (
    <div
      style={{
        ...style,
        padding: CELL_GAP / 2,
      }}
    >
      <ModelCard model={model} onDragStart={onDragStart} />
    </div>
  );
}

export function ModelGrid({
  models,
  onDragStart,
}: {
  models: ObjectAssetInfo[];
  onDragStart: (model: ObjectAssetInfo, e: MouseEvent) => void;
}) {
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const listContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!listContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    observer.observe(listContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const columnCount = useMemo(() => {
    if (containerSize.width <= 0) return 2;
    return Math.max(2, Math.floor(containerSize.width / COLUMN_MIN_WIDTH));
  }, [containerSize.width]);

  const columnWidth = useMemo(() => {
    if (containerSize.width === 0) return 0;
    return containerSize.width / columnCount;
  }, [containerSize.width, columnCount]);

  const rowCount = Math.ceil(models.length / columnCount);

  const itemData = useMemo<CellData>(
    () => ({
      models,
      columnCount,
      onDragStart,
    }),
    [models, columnCount, onDragStart],
  );

  return (
    <div ref={listContainerRef} className="h-full min-h-0 overflow-hidden">
      {containerSize.width > 0 && (
        <Grid<CellData>
          columnCount={columnCount}
          columnWidth={columnWidth}
          height={containerSize.height}
          rowCount={rowCount}
          rowHeight={columnWidth}
          width={containerSize.width}
          itemData={itemData}
          overscanRowCount={2}
        >
          {Cell}
        </Grid>
      )}
    </div>
  );
}
